import {css} from 'aphrodite';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import SchemaWarehouseNameInput from 'schemas/SchemaWarehouseNameInput';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import PartiesField from 'ui-library/inputs/PartiesField';

import ConfigureExecutionTemplateSection from './ConfigureExecutionTemplateSection';
import CanOnlyBePartOfFlowchartField from './fields/CanOnlyBePartOfFlowchartField';
import CanSetAssigneeOnTaskCreationField from './fields/CanSetAssigneeOnTaskCreationField';
import ExecutionTypeField from './fields/ExecutionTypeField';
import IsPropagateWatchersEnabledField from './fields/IsPropagateWatchersEnabledField';
import ResponsiblePartyLookupConfigField from './fields/ResponsiblePartyLookupConfigField';
import StatusLifecycleField from './fields/StatusLifecycleField';
import {
  DefaultCreationFolderSelectorContainer,
  DefaultEntryExecutionFolderSelectorContainer,
} from './ProcessSchemaFolderSelectors';
import ProcessSchemaMetadataSectionRow, {
  NameField,
  OwnerField,
  PrefixField,
} from './ProcessSchemaMetadataSectionRow';
import ProcessTaskGroupInstructionsField from './ProcessTaskGroupInstructionsField';
import {styles} from './ProcessTaskSchemaEditorStyles';
import type {ProcessTaskSchema} from './ProcessTaskSchemaUtils';
import type {ProcessTaskSchemaEditorStateResult} from './useProcessTaskSchemaEditorState';

interface TaskMetadataRowsProps {
  state: ProcessTaskSchemaEditorStateResult;
  processTaskSchema: ProcessTaskSchema | null;
  restrictedToOrganizationId?: string;
  restrictedToExecutionType?: ProcessExecutionType;
}

export default function TaskMetadataRows(props: TaskMetadataRowsProps): JSX.Element {
  const {state, processTaskSchema, restrictedToOrganizationId, restrictedToExecutionType} = props;
  const {isBeingCreated, isArchived, isLocked, isEditingFlowchart} = state;

  return (
    <>
      <ProcessSchemaMetadataSectionRow>
        <PrefixField
          label="Task group prefix"
          value={state.taskGroupPrefix}
          error={state.errors.taskGroupPrefix ?? ''}
          isBeingCreated={isBeingCreated}
          isDisabled={
            isLocked ||
            isArchived ||
            isEditingFlowchart ||
            (!isBeingCreated && BenchlingConfig.WORKFLOWS_DISABLE_CHANGING_TASK_GROUP_PREFIX)
          }
          onChange={async (updatedTaskGroupPrefix: string) => {
            if (updatedTaskGroupPrefix === state.taskGroupPrefix) {
              return;
            }
            if (!isBeingCreated) {
              await state.updateProcessTaskSchemaWithFields({
                taskGroupPrefix: updatedTaskGroupPrefix,
              });
            } else {
              state.setTaskGroupPrefix(updatedTaskGroupPrefix);
            }
          }}
          dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'prefixField'}}
        />
        <NameField
          label="Name"
          value={state.processTaskSchemaName}
          error={state.errors.processTaskSchemaName ?? ''}
          isBeingCreated={isBeingCreated}
          isDisabled={isArchived || isEditingFlowchart || isLocked}
          onChange={async (updatedName: string) => {
            if (updatedName === state.processTaskSchemaName) {
              return;
            }
            if (!isBeingCreated) {
              await state.updateProcessTaskSchemaWithFields({
                name: updatedName,
              });
            } else {
              state.setProcessTaskSchemaName(updatedName);
            }
          }}
          dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'nameField'}}
        />
        <OwnerField
          className={css(styles.ownerField)}
          isEditable={isBeingCreated && restrictedToOrganizationId == null}
          ownerChoices={state.schemaOwnerChoices}
          owner={state.schemaOwner}
          onOwnerChanged={state.setSchemaOwner}
          dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'ownerField'}}
        />
      </ProcessSchemaMetadataSectionRow>
      <ProcessSchemaMetadataSectionRow>
        <PrefixField
          label="ID prefix"
          value={state.taskSchemaPrefix}
          error={state.errors.taskSchemaPrefix ?? ''}
          isBeingCreated={isBeingCreated}
          isDisabled={isArchived || isEditingFlowchart || isLocked}
          onChange={async (updatedTaskSchemaPrefix: string) => {
            if (updatedTaskSchemaPrefix === state.taskSchemaPrefix) {
              return;
            }
            if (!isBeingCreated) {
              await state.updateProcessTaskSchemaWithFields({
                taskSchemaPrefix: updatedTaskSchemaPrefix,
              });
            } else {
              state.setTaskSchemaPrefix(updatedTaskSchemaPrefix);
            }
          }}
          prefixPreview={state.generatePrefixPreview('Task ID preview', state.taskSchemaPrefix)}
          dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'taskPrefixField'}}
        />
        <ExecutionTypeField
          value={state.executionType}
          onChange={(newExecutionType: ProcessExecutionType) => {
            state.setExecutionType(newExecutionType);
            state.setCanConfigureFlowchart(newExecutionType === ProcessExecutionType.FLOWCHART);
          }}
          isEditable={isBeingCreated && restrictedToExecutionType == null}
        />
        {state.statusLifecycle ? (
          <StatusLifecycleField
            value={state.statusLifecycle}
            onChange={state.setStatusLifecycle}
            options={state.statusLifecyclesForExecutionType}
            isBeingCreated={isBeingCreated}
            error={state.errors.statusLifecycle}
          />
        ) : (
          <div className="fieldInput-label-container">
            <FieldLabel htmlFor="" label="Status lifecycle" />
            <div className="u-small-spacing-top">
              <HorizontalThrobber />
            </div>
          </div>
        )}
      </ProcessSchemaMetadataSectionRow>
      <ProcessSchemaMetadataSectionRow>
        <ConfigureExecutionTemplateSection
          executionType={state.executionType}
          executionTemplate={state.executionTemplate}
          onChange={(onTemplateChangeArgs) => {
            state.setExecutionTemplate({
              templateCollectionId: onTemplateChangeArgs.templateCollectionId,
              name: onTemplateChangeArgs.newTemplateName,
              sourceTemplateId: onTemplateChangeArgs.sourceTemplateId,
              publishOnSchemaCreation: onTemplateChangeArgs.publishOnSchemaCreation,
            });
          }}
          isDisabled={!isBeingCreated || isArchived || isLocked}
          error={state.errors.sourceTemplate}
        />
        <DefaultCreationFolderSelectorContainer
          executionType={state.executionType}
          defaultCreationFolderId={state.defaultCreationFolderId}
          isInaccessible={state.isDefaultCreationFolderInaccessible}
          onChange={state.handleDefaultCreationFolderIdUpdate}
          isDisabled={isArchived || isLocked}
        />
        <DefaultEntryExecutionFolderSelectorContainer
          executionType={state.executionType}
          defaultEntryExecutionFolderId={state.defaultEntryExecutionFolderId}
          isInaccessible={state.isDefaultEntryExecutionFolderInaccessible}
          onChange={state.handleDefaultEntryExecutionFolderIdUpdate}
          isDisabled={isArchived || isLocked}
        />
      </ProcessSchemaMetadataSectionRow>
      <ProcessSchemaMetadataSectionRow>
        <PartiesField
          label="Default responsible parties"
          value={state.defaultResponsibleParties}
          onChange={state.handleDefaultResponsiblePartiesUpdate}
          isBeingCreated={isBeingCreated}
          isDisabled={isArchived || isEditingFlowchart || isLocked}
          setDoesNotHaveUnresolvedInput={state.setAreDefaultResponsiblePartiesValid}
          showError={state.showDefaultResponsiblePartiesError}
          dataTestAttrs={{
            component: 'ProcessTaskSchemaEditor',
            element: 'defaultResponsiblePartiesField',
          }}
          errorText='Invalid "Default responsible parties" value'
        />
        <CanSetAssigneeOnTaskCreationField
          value={state.canSetAssigneeOnTaskCreation}
          disabled={isEditingFlowchart || isLocked}
          hideWithMessage={
            state.executionType === ProcessExecutionType.FLOWCHART
              ? 'Flowchart tasks do not have assignees.'
              : null
          }
          onChange={async (value) => {
            if (value === state.canSetAssigneeOnTaskCreation) {
              return;
            }
            if (!isBeingCreated) {
              await state.updateProcessTaskSchemaWithFields({
                canSetAssigneeOnTaskCreation: value,
              });
            } else {
              state.setCanSetAssigneeOnTaskCreation(value);
            }
          }}
        />
        <CanOnlyBePartOfFlowchartField
          executionType={state.executionType}
          value={state.canOnlyBePartOfFlowchart}
          error={state.errors.canOnlyBePartOfFlowchart}
          disabled={isEditingFlowchart || isLocked}
          onChange={async (value) => {
            if (value === state.canOnlyBePartOfFlowchart) {
              return;
            }
            if (!isBeingCreated) {
              await state.updateProcessTaskSchemaWithFields({
                canOnlyBePartOfFlowchart: value,
              });
            } else {
              state.setCanOnlyBePartOfFlowchart(value);
            }
          }}
        />
      </ProcessSchemaMetadataSectionRow>
      {hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1) && (
        <ProcessSchemaMetadataSectionRow>
          <ResponsiblePartyLookupConfigField
            value={state.responsiblePartyLookupConfig}
            isBeingCreated={isBeingCreated}
            isDisabled={isArchived || isEditingFlowchart || isLocked}
            isLoading={!state.haveSchemasLoaded}
            label="Task to responsible party lookup"
            onChange={state.handleResponsiblePartyLookupConfigUpdate}
            taskSchema={processTaskSchema}
            error={state.errors.responsiblePartyLookupConfigs}
          />
        </ProcessSchemaMetadataSectionRow>
      )}
      {BenchlingConfig.SCHEMAS_ENABLE_VIEW_SYSTEM_NAME && (
        <ProcessSchemaMetadataSectionRow>
          <SchemaWarehouseNameInput
            isWritable={
              BenchlingConfig.SCHEMAS_ENABLE_CHANGE_WAREHOUSE_NAME &&
              !isArchived &&
              !isEditingFlowchart &&
              !isLocked
            }
            schema={processTaskSchema}
            sqlIdentifier={state.processTaskSchemaSqlIdentifier ?? ''}
            onSqlIdentifierChange={async (updatedSqlIdentifier: string) => {
              if (updatedSqlIdentifier === state.processTaskSchemaSqlIdentifier) {
                return;
              }
              if (!isBeingCreated) {
                await state.updateProcessTaskSchemaWithFields({
                  sqlIdentifier: updatedSqlIdentifier,
                });
              } else {
                state.setProcessTaskSchemaSqlIdentifier(updatedSqlIdentifier);
              }
            }}
            error={state.errors.sqlIdentifier}
            dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'schemaWarehouseName'}}
          />
        </ProcessSchemaMetadataSectionRow>
      )}
      <ProcessSchemaMetadataSectionRow>
        <IsPropagateWatchersEnabledField
          value={state.isPropagateWatchersEnabled}
          onChange={async (value) => {
            if (value === state.isPropagateWatchersEnabled) {
              return;
            }
            if (!isBeingCreated) {
              await state.updateProcessTaskSchemaWithFields({
                isPropagateWatchersEnabled: value,
              });
            } else {
              state.setIsPropagateWatchersEnabled(value);
            }
          }}
          disabled={isArchived || isEditingFlowchart || isLocked}
          executionType={state.executionType}
        />
      </ProcessSchemaMetadataSectionRow>
      <ProcessTaskGroupInstructionsField
        label="Instructions"
        value={state.taskGroupInstructions ?? ''}
        isBeingCreated={isBeingCreated}
        onChange={state.handleTaskGroupInstructionsUpdate}
        error={state.errors.taskGroupInstructions}
        isDisabled={isArchived || isEditingFlowchart || isLocked}
        dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'taskGroupInstructionsField'}}
      />
    </>
  );
}
